import { memo, useCallback, useContext } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Image from 'components/Common/Image'
import OfferLocationHeading from '../OfferLocationHeading'
import useFinestLocationSearchUrl from '../useFinestLocationSearchUrl'
import { hasMapCoordinates } from '../NavigableOfferContent/mapCoordinates'
import BodyText from 'LuxKit/Typography/BodyText'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import OfferPageLabelGroup from '../OfferPageLabelGroup'
import OfferRating from 'components/Common/NumberRating/OfferRating'
import KidsFlyFreeLabel from 'components/Common/Labels/KidsFlyFreeLabel'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { isMiguelMaestreHostedOffer } from 'lib/offer/offerSpecificChangesUtils'
import useKidsFlyFree from 'hooks/CharterFlights/useKidsFlyFree'
import type { FlightsClassTypes } from 'constants/flight'
import FlashTierProgressionPanel from 'loyalty/components/Banners/FlashTierProgressionPanel'
import LoyaltyStayUpgradeBenefitPill from 'loyalty/components/LoyaltyStayUpgradeBenefitPill'
import useLoyaltyUserBenefitView from 'loyalty/hooks/useLoyaltyUserBenefitView'
import Heading from 'LuxKit/Typography/Heading'
import { useIntl } from 'react-intl'
import { OfferPageHighlightTagsWithSurroundings } from '../OfferPageHighlightTags'
import { buildFlashHotelHighlightTagContext } from '../offerPageHighlightTagContextUtils'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import TextLink from 'LuxKit/TextLink'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import ModalContext from 'contexts/ModalContext'
import OfferPageFocusAnchor from 'components/OfferPage/OfferPageFocusAnchor'
import FlashHotelAboutHotelVariantModal from './FlashHotelMobileVariant/Modals/FlashHotelAboutHotelVariantModal'

const PropertyLogo = styled(Image)`
  display: none;

  ${mediaQueryUp.tablet} {
    margin-inline-start: ${rem(16)};
    display: block;
    height: ${rem(75)};
  }
`

const ContentsCSSBreakpoint = styled(CSSBreakpoint)`
  display: contents;
`

interface Props {
  offer: App.HotelOffer
  property: App.OfferProperty
  suppressTakeoverCampaignTagWhenUSPVisible?: boolean
  charterBundleRoute?: App.CharterBundleRoute
  freeStayLabel?: string
  fareClass?: FlightsClassTypes
  packageViews?: Array<App.OfferPackageOptionView>
  checkoutDate?: string
  checkIn?: string
  currentDuration?: number
  isOccupancyContextKnown: boolean
  rooms?: Array<App.RoomOccupants>
}

function FlashHotelPropertyDetails(props: Props) {
  const {
    offer,
    property,
    suppressTakeoverCampaignTagWhenUSPVisible = false,
    charterBundleRoute,
    freeStayLabel,
    fareClass,
    packageViews,
    checkoutDate,
    checkIn,
    currentDuration,
    isOccupancyContextKnown,
    rooms,
  } = props

  const searchUrl = useFinestLocationSearchUrl(offer.id)
  const isMobileDevice = useScreenSizeOnly('mobile')
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const highlightTagContextBuilder = useCallback(
    () => buildFlashHotelHighlightTagContext(offer, property, packageViews, charterBundleRoute),
    [charterBundleRoute, offer, packageViews, property],
  )
  const { shouldShowKidsFlyFreeForOffer } = useKidsFlyFree({ charterBundleRoute, fareClass })
  const isNewMobileHeaderUIEnabled = isMobileDevice
  const location = [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ')
  const loyaltyRoomUpgradeBenefitDetails = useLoyaltyUserBenefitView('hotel_upgrade')
  const hasUpgradablePackages = offer.loyalty.hasPackageTypeUpgrades || offer.loyalty.hasRoomTypeUpgrades

  const propertyNameOrFallback = offer.property.name || intl.formatMessage({
    id: '1K3EXrzCdqY8du2zz77A',
    defaultMessage: 'Vendor',
    description: 'Fallback text for vendor when property name is not available',
  })

  let propertyNameWithFlights: string
  if (!charterBundleRoute) {
    propertyNameWithFlights = offer.property.name
  } else if (isMiguelMaestreHostedOffer(offer.id)) {
    propertyNameWithFlights = intl.formatMessage(
      {
        id: 'dxj0xmHDy1QiisV-AY3a',
        defaultMessage: '{propertyName} & Flights with Miguel Maestre',
        description: 'Offer page title for the charter bundle departure hosted by Miguel Maestre, shown as the offer detail page heading',
      },
      { propertyName: propertyNameOrFallback },
    )
  } else {
    propertyNameWithFlights = intl.formatMessage(
      {
        id: 'h7QcmO8fvNLHQYmsH98L',
        defaultMessage: '{propertyName} & Flights',
        description: 'Property name with flights suffix for charter bundle offers',
      },
      { propertyName: propertyNameOrFallback },
    )
  }

  const openAboutHotelVariantModal = useCallback(() => {
    showModal(
      <FlashHotelAboutHotelVariantModal
        descriptionMarkdown={offer.description}
        facilitiesMarkdown={offer.facilities}
        location={location}
        offerName={offer.name}
        propertyName={propertyNameWithFlights}
      />,
    )
  }, [location, offer.description, offer.facilities, offer.name, propertyNameWithFlights, showModal])

  return (
    <Group direction="vertical">
      <OfferPageFocusAnchor targetId="page:overview" anchorRelation="next-sibling" />
      <Group direction="vertical" gap={8} tabletGap={20}>
        {isNewMobileHeaderUIEnabled && (
          <ContentsCSSBreakpoint max="mobile">
            <OfferPageLabelGroup
              offer={offer}
              showBadge
              suppressTakeoverCampaignTagWhenUSPVisible={suppressTakeoverCampaignTagWhenUSPVisible}
              freeStayLabel={freeStayLabel}
            />
          </ContentsCSSBreakpoint>
        )}
        <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
          <Group direction="vertical" gap={8}>
            <OfferLocationHeading
              location={location}
              searchUrl={searchUrl}
              showMapLink={hasMapCoordinates(property)}
            />
            <Heading variant="heading2" as="h1">{propertyNameWithFlights}</Heading>
            <ContentsCSSBreakpoint max="mobile">
              <Group direction="vertical" gap={8} noDisplayWhenEmpty>
                {shouldShowKidsFlyFreeForOffer(offer, checkIn) && <KidsFlyFreeLabel />}
                {isOfferRatingDisplayable(property.rating) && <OfferRating
                  link
                  inlineLabel
                  variant="xs"
                  rating={property.rating}
                />}
                {!isNewMobileHeaderUIEnabled && (
                  <OfferPageLabelGroup
                    offer={offer}
                    showBadge
                    suppressTakeoverCampaignTagWhenUSPVisible={suppressTakeoverCampaignTagWhenUSPVisible}
                    freeStayLabel={freeStayLabel}
                  />
                )}
              </Group>
            </ContentsCSSBreakpoint>
            <BodyText variant="large">{offer.name}</BodyText>
            {isNewMobileHeaderUIEnabled && (
              <ContentsCSSBreakpoint max="mobile">
                <Group direction="horizontal" horizontalAlign="end">
                  <TextLink endIcon={<LineAngleRightIcon />} onClick={openAboutHotelVariantModal}>
                    {intl.formatMessage({
                      id: '0lHz4xJEAd3-zpEcHAFu',
                      defaultMessage: 'Read more',
                      description: 'Link text that expands or navigates to more offer details on the offer detail page',
                    })}
                  </TextLink>
                </Group>
              </ContentsCSSBreakpoint>
            )}
          </Group>
          {property.logoImageId &&
            <PropertyLogo
              dpr={2}
              height={75}
              enhancedFormats={false}
              id={property.logoImageId}
              alt={intl.formatMessage(
                {
                  id: 'OAyYxWPMQaAP31G2VZFo',
                  defaultMessage: '{propertyName} logo',
                  description: 'Accessibility alt text for property logo image on the offer detail page',
                },
                {
                  propertyName: property.name || intl.formatMessage({
                    id: '1K3EXrzCdqY8du2zz77A',
                    defaultMessage: 'Vendor',
                    description: 'Fallback text for vendor when property name is not available',
                  }),
                },
              )}
            />}
        </Group>
        {isOfferRatingDisplayable(property.rating) && (
          <ContentsCSSBreakpoint min="tablet">
            <OfferRating
              link
              inlineLabel
              variant="small"
              rating={property.rating}
            />
          </ContentsCSSBreakpoint>
        )}
        <OfferPageHighlightTagsWithSurroundings
          contextBuilder={highlightTagContextBuilder}
          duration={currentDuration}
          hasSelectedDates={!!checkIn}
          isOccupancyContextKnown={isOccupancyContextKnown}
          offerPageHighlightTagsOverride={offer.offerPageHighlightTagsOverride}
          propertyId={property.id}
          rooms={rooms}
          surroundingsOfferId={offer.id}
        />
        {(hasUpgradablePackages && !!loyaltyRoomUpgradeBenefitDetails?.availability) && <LoyaltyStayUpgradeBenefitPill offer={offer} hideViewOfferCta />}
        <ContentsCSSBreakpoint min="tablet">
          <FlashTierProgressionPanel offer={offer} packageViews={packageViews} checkoutDate={checkoutDate} />
        </ContentsCSSBreakpoint>
      </Group>
    </Group>
  )
}

export default memo(FlashHotelPropertyDetails)
